// ============================================================
// ARCHIVO: src/components/TabCalificaciones.jsx
// Vista acordeón: aspirante → programa → bloque → módulos/actividades
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, Stack, TextField, MenuItem,
  Chip, Alert, CircularProgress, Divider,
  IconButton, Tooltip, Collapse, TablePagination,
} from "@mui/material";
import { FiRefreshCw, FiSearch, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { getAuth } from "firebase/auth";

const API_BASE = "https://vol-backend.onrender.com";

const COLORS = {
  bg: "#f5f0ff", white: "#ffffff", whiteSoft: "#fff8ff",
  subtle: "#e6dfef", red: "#ff3333", redDark: "#cc0000",
  textMain: "#2d233a", textMuted: "#6c6478",
  green: "#1b5e20", greenBg: "#e8f5e9",
  orange: "#e65100", orangeBg: "#fff3e0",
  danger: "#b00020", dangerBg: "#ffebee",
  blue: "#0d47a1", blueBg: "#e3f2fd",
  gray: "#555", grayBg: "#f5f5f5",
};

// ─── Auth helper ──────────────────────────────────────────────
async function authedFetch(url, opts = {}) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("No autenticado");
  const token = await user.getIdToken();
  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}`, "x-firebase-uid": user.uid },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
  return res.json();
}

function safeStr(v) { return v === null || v === undefined ? "" : String(v); }
function fullName(r) {
  return `${safeStr(r?.nombre)} ${safeStr(r?.apellido_pat)}`.replace(/\s+/g, " ").trim();
}
function safeJson(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v); } catch { return []; }
}

// ─── Tipo de bloque ───────────────────────────────────────────
function tipoBloque(row) {
  const practica = Number(row.has_practical_exam) === 1;
  const conEval  = Number(row.total_quiz) > 0;
  if (practica) return { label: "Teórico con Evaluación y Práctica", color: "#4a148c", bg: "#f3e5f5" };
  if (conEval)  return { label: "Teórico con Evaluación",            color: "#01579b", bg: "#e1f5fe" };
  return           { label: "Teórico",                               color: "#1b5e20", bg: "#e8f5e9" };
}

// ─── Pequeños chips y badges ──────────────────────────────────
function ScoreBadge({ score }) {
  if (score === null || score === undefined || score === "")
    return <span style={{ color: "#aaa", fontSize: 12 }}>—</span>;
  const n = Number(score);
  const color = n >= 8 ? COLORS.green : n >= 6 ? COLORS.orange : COLORS.danger;
  const bg    = n >= 8 ? COLORS.greenBg : n >= 6 ? COLORS.orangeBg : COLORS.dangerBg;
  return (
    <Box sx={{ display: "inline-block", px: 0.8, py: 0.2, borderRadius: 1, backgroundColor: bg, color, fontWeight: 800, fontSize: 12 }}>
      {n.toFixed(2)}
    </Box>
  );
}

function EstadoChip({ status, size = "small" }) {
  const map = {
    aprobado:    { label: "Aprobado",    bg: COLORS.greenBg,  color: COLORS.green },
    reprobado:   { label: "Reprobado",   bg: COLORS.dangerBg, color: COLORS.danger },
    en_progreso: { label: "En proceso",  bg: COLORS.orangeBg, color: COLORS.orange },
    completed:   { label: "Completado",  bg: COLORS.blueBg,   color: COLORS.blue },
    graded:      { label: "Calificado",  bg: COLORS.greenBg,  color: COLORS.green },
    approved:    { label: "Aprobado",    bg: COLORS.greenBg,  color: COLORS.green },
  };
  const s = map[status] || { label: safeStr(status) || "—", bg: COLORS.grayBg, color: COLORS.gray };
  return (
    <Chip label={s.label} size={size}
      sx={{ fontSize: 10, height: 20, fontWeight: 700, backgroundColor: s.bg, color: s.color, border: "none" }} />
  );
}

function ActPill({ label, score, status, tipo }) {
  const completado = ["completed", "graded", "approved"].includes(status);
  const sinEntregar = !completado && !score;

  let bg, color, texto;
  if (score !== null && score !== undefined) {
    const n = Number(score);
    bg    = n >= 8 ? COLORS.greenBg  : COLORS.dangerBg;
    color = n >= 8 ? COLORS.green    : COLORS.danger;
    texto = completado ? `✓ ${n.toFixed(2)}` : n.toFixed(2);
  } else if (completado) {
    bg = COLORS.blueBg; color = COLORS.blue; texto = "Completado";
  } else if (sinEntregar) {
    // Upload sin entregar vs complementaria sin ver
    const esUpload = ["upload","docs","documento"].includes(tipo);
    bg = esUpload ? COLORS.dangerBg : COLORS.grayBg;
    color = esUpload ? COLORS.danger : COLORS.gray;
    texto = esUpload ? "✗ Sin entregar" : "—";
  } else {
    bg = COLORS.orangeBg; color = COLORS.orange; texto = "En proceso";
  }

  return (
    <Box sx={{ textAlign: "center", minWidth: 80 }}>
      <Typography sx={{ fontSize: 10, color: COLORS.textMuted, mb: 0.3, lineHeight: 1.2 }}>{label}</Typography>
      <Box sx={{ px: 1, py: 0.3, borderRadius: 1, backgroundColor: bg, color, fontWeight: 800, fontSize: 12, display: "inline-block", minWidth: 60 }}>
        {texto}
      </Box>
    </Box>
  );
}

// ─── Detalle de un bloque ─────────────────────────────────────
function BloqueDetalle({ row }) {
  const modulos      = safeJson(row.modulos_detalle);
  const uploads      = safeJson(row.uploads_detalle);
  const examenes     = safeJson(row.examenes_detalle);
  const complementarias = safeJson(row.complementarias_detalle);

  // Construir mapa module_id → { uploads, examenes, complementarias }
  const actsByMod = useMemo(() => {
    const map = {};
    const ensure = (id) => { if (!map[id]) map[id] = { uploads: [], examenes: [], comp: [] }; };

    uploads.forEach(a => { ensure(a.module_id); map[a.module_id].uploads.push(a); });
    examenes.forEach(a => { ensure(a.module_id); map[a.module_id].examenes.push(a); });
    complementarias.forEach(a => { ensure(a.module_id); map[a.module_id].comp.push(a); });
    return map;
  }, [uploads, examenes, complementarias]);

  const tipo = tipoBloque(row);
  const hasPractica = Number(row.has_practical_exam) === 1;

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {/* Encabezado del bloque */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
        <Chip label={tipo.label} size="small"
          sx={{ fontSize: 10, height: 20, fontWeight: 700, backgroundColor: tipo.bg, color: tipo.color }} />
        {hasPractica && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>Práctica:</Typography>
            <ScoreBadge score={row.practica_final} />
          </Stack>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>Promedio teórico:</Typography>
          <ScoreBadge score={row.promedio_teorico_bloque} />
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>Nota final:</Typography>
          <ScoreBadge score={row.nota_final_bloque} />
        </Stack>
        <EstadoChip status={row.bloque_status} />
      </Stack>

      {/* Módulos */}
      {modulos.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>Sin módulos registrados.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {[...modulos].sort((a, b) => (a.orden || 0) - (b.orden || 0)).map((mod) => {
            const mActs = actsByMod[mod.module_id] || { uploads: [], examenes: [], comp: [] };
            const tieneActividades = mActs.uploads.length + mActs.examenes.length + mActs.comp.length > 0;

            return (
              <Box key={mod.module_id}
                sx={{ border: `0.5px solid ${COLORS.subtle}`, borderRadius: 2, overflow: "hidden" }}>
                {/* Header módulo */}
                <Box sx={{ px: 1.5, py: 0.8, backgroundColor: COLORS.whiteSoft,
                  borderBottom: tieneActividades ? `0.5px solid ${COLORS.subtle}` : "none" }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.textMain, flex: 1 }}>
                      {safeStr(mod.nombre)}
                    </Typography>
                    <EstadoChip status={mod.status} />
                    {mod.uploads_avg !== null && mod.uploads_avg !== undefined && (
                      <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>
                        Entregas: <ScoreBadge score={mod.uploads_avg} />
                      </Typography>
                    )}
                    {mod.quiz_score !== null && mod.quiz_score !== undefined && (
                      <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>
                        Examen: <ScoreBadge score={mod.quiz_score} />
                      </Typography>
                    )}
                    {mod.extraordinary_score !== null && mod.extraordinary_score !== undefined && (
                      <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>
                        Extraordinario: <ScoreBadge score={mod.extraordinary_score} />
                      </Typography>
                    )}
                    {mod.theoretical_grade !== null && mod.theoretical_grade !== undefined && (
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted }}>
                        Final: <ScoreBadge score={mod.theoretical_grade} />
                      </Typography>
                    )}
                  </Stack>
                </Box>

                {/* Actividades del módulo */}
                {tieneActividades && (
                  <Box sx={{ px: 1.5, py: 1 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {/* Complementarias (videos, lecturas, etc.) */}
                      {mActs.comp.map((a, i) => (
                        <ActPill key={`comp-${i}`}
                          label={safeStr(a.nombre || a.name).slice(0, 22) || `Act ${i + 1}`}
                          score={null}
                          status={a.completado === 1 || a.completado === "1" ? "completed" : "not_started"}
                          tipo={safeStr(a.tipo || a.type)} />
                      ))}
                      {/* Uploads / entregas */}
                      {mActs.uploads.map((a, i) => (
                        <ActPill key={`up-${i}`}
                          label={`Entrega ${i + 1}`}
                          score={a.nota ?? a.score ?? null}
                          status={a.estado === "calificado" ? "graded"
                            : a.estado === "pendiente" ? "in_progress"
                            : "not_started"}
                          tipo="upload" />
                      ))}
                      {/* Exámenes */}
                      {mActs.examenes.map((a, i) => {
                        const esExtra = safeStr(a.tipo || a.type) === "extraordinary";
                        return (
                          <ActPill key={`ex-${i}`}
                            label={esExtra ? "Extraordinario" : `Examen ${i + 1}`}
                            score={a.score ?? a.calificacion ?? null}
                            status={safeStr(a.status || a.actividad_status)}
                            tipo={safeStr(a.tipo || a.type)} />
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

// ─── Fila de aspirante con acordeón ──────────────────────────
function AspiranteRow({ programaRows, expanded, onToggleUser, onTogglePrograma }) {
  const firstRow = programaRows[0] || {};
  const userId = safeStr(firstRow.user_id);

  // Agrupar por programa
  const programas = useMemo(() => {
    const map = {};
    programaRows.forEach(row => {
      const key = safeStr(row.programa_code);
      if (!map[key]) map[key] = { code: key, name: safeStr(row.programa_nombre), bloques: [] };
      map[key].bloques.push(row);
    });
    return Object.values(map);
  }, [programaRows]);

  const userExpanded = expanded.users[userId];

  return (
    <Box sx={{ border: `0.5px solid ${COLORS.subtle}`, borderRadius: 2, overflow: "hidden", mb: 1 }}>
      {/* Cabecera aspirante */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{
        px: 2, py: 1.2, backgroundColor: COLORS.whiteSoft,
        cursor: "pointer", "&:hover": { backgroundColor: COLORS.subtle + "44" },
      }} onClick={() => onToggleUser(userId)}>
        {/* Avatar */}
        <Box sx={{
          width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
          backgroundColor: COLORS.subtle, display: "flex", alignItems: "center",
          justifyContent: "center", fontWeight: 700, fontSize: 13, color: COLORS.textMain,
        }}>
          {(fullName(firstRow)[0] || "?").toUpperCase()}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.textMain }}>
            {fullName(firstRow) || "—"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>
            {safeStr(firstRow.matricula)} · {safeStr(firstRow.estado_usuario)}
          </Typography>
        </Box>

        {/* Chips resumen de programas */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {programas.map(p => (
            <Chip key={p.code} label={p.code} size="small"
              sx={{ fontSize: 10, height: 18, fontWeight: 700,
                backgroundColor: COLORS.white, border: `0.5px solid ${COLORS.subtle}` }} />
          ))}
        </Stack>

        <IconButton size="small" onClick={e => { e.stopPropagation(); onToggleUser(userId); }}>
          {userExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
        </IconButton>
      </Stack>

      {/* Acordeón de programas */}
      <Collapse in={!!userExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.5, py: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
          {programas.map(prog => {
            const progKey = `${userId}-${prog.code}`;
            const progExpanded = expanded.programas[progKey];

            return (
              <Box key={prog.code}
                sx={{ border: `0.5px solid ${COLORS.subtle}`, borderRadius: 1.5, overflow: "hidden" }}>
                {/* Cabecera programa */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{
                  px: 1.5, py: 1, cursor: "pointer", backgroundColor: COLORS.white,
                  "&:hover": { backgroundColor: COLORS.whiteSoft },
                }} onClick={() => onTogglePrograma(progKey)}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.textMain, flex: 1 }}>
                    {prog.name}
                    <Typography component="span" sx={{ fontSize: 10, color: COLORS.textMuted, ml: 0.5 }}>
                      ({prog.code})
                    </Typography>
                  </Typography>

                  {/* Mini resumen bloques */}
                  <Stack direction="row" spacing={0.5}>
                    {prog.bloques.map((b, i) => (
                      <Tooltip key={i} title={`${safeStr(b.bloque_nombre)}: ${safeStr(b.bloque_status) || "sin datos"}`}>
                        <Box sx={{
                          width: 10, height: 10, borderRadius: "50%",
                          backgroundColor:
                            b.bloque_status === "aprobado"    ? COLORS.green :
                            b.bloque_status === "reprobado"   ? COLORS.danger :
                            b.bloque_status === "en_progreso" ? COLORS.orange : COLORS.gray,
                        }} />
                      </Tooltip>
                    ))}
                  </Stack>

                  <IconButton size="small">
                    {progExpanded ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
                  </IconButton>
                </Stack>

                {/* Bloques del programa */}
                <Collapse in={!!progExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ backgroundColor: COLORS.bg }}>
                    {[...prog.bloques].sort((a, b) => (a.bloque_orden || 0) - (b.bloque_orden || 0))
                      .map((bloqueRow, bi) => {
                        const tipo = tipoBloque(bloqueRow);
                        return (
                          <Box key={bloqueRow.block_id}
                            sx={{ borderTop: bi > 0 ? `0.5px solid ${COLORS.subtle}` : "none" }}>
                            {/* Header bloque */}
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 0.8 }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, minWidth: 60 }}>
                                Bloque {bloqueRow.bloque_orden}
                              </Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.textMain, flex: 1 }}>
                                {safeStr(bloqueRow.bloque_nombre)}
                              </Typography>
                              <Chip label={tipo.label} size="small"
                                sx={{ fontSize: 9, height: 18, fontWeight: 600,
                                  backgroundColor: tipo.bg, color: tipo.color }} />
                              <EstadoChip status={bloqueRow.bloque_status} />
                            </Stack>

                            {/* Detalle del bloque */}
                            <Box sx={{ borderTop: `0.5px solid ${COLORS.subtle}99`, backgroundColor: COLORS.white }}>
                              <BloqueDetalle row={bloqueRow} />
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────
export default function TabCalificaciones() {
  const [programa, setPrograma]   = useState("");
  const [estado, setEstado]       = useState("");
  const [matricula, setMatricula] = useState("");
  const [bloque, setBloque]       = useState("");
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const [programas, setProgramas]             = useState([]);
  const [estadosScope, setEstadosScope]       = useState([]);
  const [groupCodesScope, setGroupCodesScope] = useState([]);
  const [esAdmin, setEsAdmin]                 = useState(false);
  const [bloques, setBloques]                 = useState([]);

  // Estado del acordeón: { users: {uid: bool}, programas: {uid-code: bool} }
  const [expanded, setExpanded] = useState({ users: {}, programas: {} });

  const onToggleUser = (uid) =>
    setExpanded(prev => ({ ...prev, users: { ...prev.users, [uid]: !prev.users[uid] } }));

  const onTogglePrograma = (key) =>
    setExpanded(prev => ({ ...prev, programas: { ...prev.programas, [key]: !prev.programas[key] } }));

  // Cargar meta del coordinador
  useEffect(() => {
    authedFetch(`${API_BASE}/progreso/admin/evaluar/programas`)
      .then(d => {
        setProgramas(Array.isArray(d?.programs) ? d.programs : []);
        setEstadosScope(Array.isArray(d?.estados) ? d.estados : []);
        setGroupCodesScope(Array.isArray(d?.groupCodes) ? d.groupCodes : []);
        setEsAdmin(!d?.estados || d.estados === null);
      })
      .catch(() => {});
  }, []);

  // Cargar bloques cuando cambia el programa
  useEffect(() => {
    if (!programa) { setBloques([]); setBloque(""); return; }
    authedFetch(`${API_BASE}/progreso/admin/evaluar/calificaciones?programa=${programa}&limit=100`)
      .then(d => {
        const rows = d?.calificaciones || [];
        const seen = new Set();
        const unique = [];
        rows.forEach(r => {
          if (!seen.has(r.block_id)) {
            seen.add(r.block_id);
            unique.push({ block_id: r.block_id, nombre: r.bloque_nombre, orden: r.bloque_orden });
          }
        });
        setBloques(unique.sort((a, b) => a.orden - b.orden));
      })
      .catch(() => setBloques([]));
  }, [programa]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE}/progreso/admin/evaluar/calificaciones`);
      url.searchParams.set("page", page + 1);
      url.searchParams.set("limit", rowsPerPage);
      if (programa) url.searchParams.set("programa", programa);
      if (bloque)   url.searchParams.set("bloque", bloque);
      if (matricula.trim()) url.searchParams.set("matricula", matricula.trim());
      if (estado) {
        if (groupCodesScope.length > 0) url.searchParams.set("group_code", estado);
        else url.searchParams.set("estado", estado);
      }
      const res = await authedFetch(url.toString());
      setData(res);
      setExpanded({ users: {}, programas: {} });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [programa, bloque, estado, matricula, page, rowsPerPage, groupCodesScope]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Agrupar filas por aspirante
  const aspirantes = useMemo(() => {
    const rows = data?.calificaciones || [];
    const map = {};
    rows.forEach(row => {
      const uid = safeStr(row.user_id);
      if (!map[uid]) map[uid] = [];
      map[uid].push(row);
    });
    return Object.entries(map).map(([uid, rows]) => ({ uid, rows }));
  }, [data]);

  const total = data?.pagination?.total || 0;

  // Lista de estados para el selector
  const estadoOpciones = esAdmin
    ? ["Aguascalientes","Baja California","Baja California Sur","Campeche","Chiapas","Chihuahua",
       "Ciudad de México","Coahuila","Colima","Durango","Estado de México","Guanajuato","Guerrero",
       "Hidalgo","Jalisco","Michoacán","Morelos","Nayarit","Nuevo León","Oaxaca","Puebla",
       "Querétaro","Quintana Roo","San Luis Potosí","Sinaloa","Sonora","Tabasco","Tamaulipas",
       "Tlaxcala","Veracruz","Yucatán","Zacatecas"]
    : groupCodesScope.length > 0 ? groupCodesScope : estadosScope;

  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.subtle}`, mb: 2 }}>

        {/* ── Filtros ── */}
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.2} alignItems="center" sx={{ mb: 1.5 }}>
          <TextField select size="small" label="Programa" value={programa}
            onChange={e => { setPrograma(e.target.value); setBloque(""); setPage(0); }}
            sx={{ width: 260, backgroundColor: COLORS.white }}>
            <MenuItem value="">Todos los programas</MenuItem>
            {programas.map(p => <MenuItem key={p.code} value={p.code}>{p.name} ({p.code})</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Bloque" value={bloque} disabled={!programa}
            onChange={e => { setBloque(e.target.value); setPage(0); }}
            sx={{ width: 220, backgroundColor: COLORS.white }}>
            <MenuItem value="">Todos los bloques</MenuItem>
            {bloques.map(b => <MenuItem key={b.block_id} value={b.block_id}>{b.nombre}</MenuItem>)}
          </TextField>

          <TextField select size="small"
            label={groupCodesScope.length > 0 ? "Grupo AlEn" : "Estado"}
            value={estado}
            onChange={e => { setEstado(e.target.value); setPage(0); }}
            sx={{ width: 200, backgroundColor: COLORS.white }}>
            <MenuItem value="">
              {groupCodesScope.length > 0 ? "Todos mis grupos" : "Todos mis estados"}
            </MenuItem>
            {estadoOpciones.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
          </TextField>

          <TextField size="small" label="Matrícula" value={matricula}
            onChange={e => { setMatricula(e.target.value); setPage(0); }}
            sx={{ width: 160, backgroundColor: COLORS.white }}
            InputProps={{ endAdornment:
              <Tooltip title="Buscar"><IconButton onClick={fetchData}><FiSearch /></IconButton></Tooltip> }}
          />

          <Tooltip title="Refrescar">
            <IconButton onClick={fetchData}
              sx={{ border: `1px solid ${COLORS.subtle}`, borderRadius: 2, backgroundColor: COLORS.white }}>
              <FiRefreshCw />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Leyenda de tipos de bloque */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
          {[
            { label: "Teórico", color: "#1b5e20", bg: "#e8f5e9", desc: "Solo completar actividades" },
            { label: "Teórico con Evaluación", color: "#01579b", bg: "#e1f5fe", desc: "Actividades + examen y entregas" },
            { label: "Teórico con Evaluación y Práctica", color: "#4a148c", bg: "#f3e5f5", desc: "Todo lo anterior + evaluación práctica" },
          ].map(t => (
            <Tooltip key={t.label} title={t.desc}>
              <Chip label={t.label} size="small"
                sx={{ fontSize: 10, height: 20, fontWeight: 600, backgroundColor: t.bg, color: t.color, cursor: "help" }} />
            </Tooltip>
          ))}
          <Typography sx={{ fontSize: 10, color: COLORS.textMuted, alignSelf: "center" }}>
            — Pasa el cursor sobre el tipo para ver los requisitos
          </Typography>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{error}</Alert>}
        <Divider sx={{ mb: 1.5, borderColor: COLORS.subtle }} />

        {/* ── Lista de aspirantes ── */}
        {loading ? (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={18} />
            <Typography sx={{ color: COLORS.textMuted }}>Cargando…</Typography>
          </Stack>
        ) : aspirantes.length === 0 ? (
          <Typography sx={{ color: COLORS.textMuted, textAlign: "center", py: 4 }}>
            Sin resultados. Selecciona un programa para comenzar.
          </Typography>
        ) : (
          <Box>
            {aspirantes.map(({ uid, rows }) => (
              <AspiranteRow
                key={uid}
                programaRows={rows}
                expanded={expanded}
                onToggleUser={onToggleUser}
                onTogglePrograma={onTogglePrograma}
              />
            ))}
          </Box>
        )}

        <TablePagination
          component="div" count={total} page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por página:"
        />
      </Paper>
    </Box>
  );
}